import { useMemo } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';

import { Direction, useDirection } from '@providers/Direction';

import { Theme } from './theme';
import { useTheme } from './ThemeProvider';
import { BreakpointKey, StyleObj } from './types';

const mobileFirstBreakpoints: BreakpointKey[] = ['xs', 'sm', 'md', 'lg', 'xl'];

// styles object with responsiveness overrides
type StyleObjDef = StyleObj & {
  on?: {
    [key in BreakpointKey]?: StyleObj;
  };
};

type StyleObjDefCallback<P extends object | null> = (props: P) => StyleObjDef;

type StylesDef<P extends object | null, ClassKey extends string = string> =
  | Record<ClassKey, StyleObjDef | StyleObjDefCallback<P>>
  | ((theme: Theme) => Record<ClassKey, StyleObjDef | StyleObjDefCallback<P>>);

type UseStylesResult<ClassKey extends string = string> = Record<
  ClassKey,
  StyleObj
>;

type UseStyles<ClassKey extends string = string> =
  () => UseStylesResult<ClassKey>;
type UseStylesWithProps<P extends object, ClassKey extends string = string> = (
  props: P,
) => UseStylesResult<ClassKey>;

const flippableKeys: Record<string, string> = {
  marginLeft: 'marginRight',
  marginRight: 'marginLeft',
  paddingLeft: 'paddingRight',
  paddingRight: 'paddingLeft',
  borderRightWidth: 'borderLeftWidth',
  borderLeftWidth: 'borderRightWidth',
  borderRightColor: 'borderLeftColor',
  borderLeftColor: 'borderRightColor',
};

const flippableValues: Record<string, string> = {
  row: 'row-reverse',
  'row-reverse': 'row',
  'flex-start': 'flex-end',
  'flex-end': 'flex-start',
  marginLeft: 'marginRight',
  marginRight: 'marginLeft',
};

function processDirectionFlipping(
  obj: StyleObj,
  direction: Direction,
): StyleObj {
  if (direction === 'ltr') {
    return obj;
  }

  const result: StyleObj = {};

  Object.entries(obj).forEach(([key, value]) => {
    const flipped = flippableKeys[key] ?? key;
    result[flipped as 'backgroundColor'] = value;

    // flip VALUES (row ↔ row-reverse, flex-start ↔ flex-end)
    if (key === 'flexDirection' || key === 'alignItems') {
      const flippedValue =
        flippableValues[value as string] ?? value;
      (result as any)[key] = flippedValue;
    }

    // special case: margins must stay NUMBERS
    if (key === 'marginLeft' && typeof value === 'number') {
      result.marginRight = value;
    }

    if (key === 'marginRight' && typeof value === 'number') {
      result.marginLeft = value;
    }
  });

  return result;
}

function computeStyleObj<P extends object | null>(
  styleObjDefOrPredicate: StyleObjDef | StyleObjDefCallback<P>,
  theme: Theme,
  width: number,
  props: P,
  direction: Direction,
): StyleObj {
  const styleObjectDef =
    typeof styleObjDefOrPredicate === 'function'
      ? styleObjDefOrPredicate(props!)
      : styleObjDefOrPredicate;

  const { on, ...rest } = styleObjectDef;
  const result: StyleObj = rest;

  if (on) {
    mobileFirstBreakpoints.forEach((breakpointKey) => {
      const breakpointStyles = on[breakpointKey];

      if (!breakpointStyles) {
        return;
      }

      const breakpointValue = theme.breakpoints[breakpointKey];

      // matching
      if (width >= breakpointValue) {
        Object.entries(breakpointStyles).forEach(([key, value]) => {
          result[key as 'backgroundColor'] = value;
        });
      }
    });
  }

  return processDirectionFlipping(result, direction);
}

function computeStyles<
  P extends object | null,
  T extends object = {},
  S = StylesDef<P>,
>(
  stylesDefOrStylesDefCallback: S,
  theme: Theme,
  width: number,
  height: number,
  props: P,
  direction: Direction,
) {
  const stylesResult: Record<keyof typeof stylesDef, StyleObj> = {} as any;


  theme.screen = {
    width,
    height,
  };
  theme.direction = direction;

  const stylesDef =
    typeof stylesDefOrStylesDefCallback === 'function'
      ? stylesDefOrStylesDefCallback(theme)
      : stylesDefOrStylesDefCallback;

  Object.entries(stylesDef).forEach(([key, value]) => {
    stylesResult[key] = computeStyleObj<P>(
      value as StyleObjDef,
      theme,
      width,
      props,
      direction,
    );
  });

  return StyleSheet.create(stylesResult) as Record<keyof T, StyleObj>;
}

export function makeStyles<P extends null, ClassKey extends string = string>(
  stylesDef: StylesDef<P, ClassKey>,
): UseStyles<ClassKey>;
export function makeStyles<P extends object, ClassKey extends string = string>(
  stylesDef: StylesDef<P, ClassKey>,
): UseStylesWithProps<P, ClassKey>;
export function makeStyles<
  P extends object | null,
  ClassKey extends string = string,
>(stylesDef: StylesDef<P, ClassKey>) {
  return function useStyles(props?: P) {
    const { width, height } = useWindowDimensions();
    const theme = useTheme();
    const { direction } = useDirection();

    const styles = useMemo(() => {
      return computeStyles(
        stylesDef,
        theme,
        width,
        height,
        props ?? {},
        direction,
      );
    }, [theme, width, height, props, direction]);

    return styles;
  };
}
